import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase, Profile, getProfile, isDemoModeFlag } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  loading: boolean
  signUp: (email: string, password: string, metadata?: any) => Promise<any>
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<any>
  updateProfile: (updates: Partial<Profile>) => Promise<Profile | null>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // In demo mode, immediately set loading to false
    if (isDemoModeFlag) {
      console.log('Demo mode: Skipping auth initialization')
      setLoading(false)
      return
    }

    let isMounted = true
    
    // Set a safety timeout to prevent infinite loading (reduced to 1.5 seconds for faster response)
    const safetyTimeout = setTimeout(() => {
      console.warn('Auth initialization timeout - forcing loading to false')
      if (isMounted) {
        setLoading(false)
      }
    }, 1500) // 1.5 second safety timeout

    // Initialize authentication
    const initializeAuth = async () => {
      try {
        console.log('Initializing authentication...')
        console.log('LocalStorage keys:', Object.keys(localStorage).filter(key => key.includes('supabase')))
        
        // Get initial session - this will restore from localStorage
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          if (isMounted) {
            clearTimeout(safetyTimeout)
            setLoading(false)
          }
          return
        }

        if (session?.user) {
          console.log('✅ Session restored for user:', session.user.email)
          console.log('Session expires at:', new Date(session.expires_at! * 1000).toLocaleString())
          if (isMounted) {
            setSession(session)
            setUser(session.user)
            clearTimeout(safetyTimeout)
            // Load profile but set loading to false immediately
            setLoading(false)
            loadProfile(session.user.id, false)
          }
        } else {
          console.log('❌ No existing session found')
          if (isMounted) {
            clearTimeout(safetyTimeout)
            setLoading(false)
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (isMounted) {
          clearTimeout(safetyTimeout)
          setLoading(false)
        }
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email || 'No session')
        
        if (!isMounted) return

        // Update auth state
        setSession(session)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          // Load profile when user signs in, but don't block on it
          loadProfile(session.user.id, false).catch(error => {
            console.error('Error loading profile after auth change:', error)
          })
        } else {
          // Clear profile when user signs out
          setProfile(null)
        }
        
        // Always ensure loading is set to false after auth state change
        setLoading(false)
        console.log('Auth state change processed, loading set to false')
      }
    )

    // Start initialization
    initializeAuth()

    return () => {
      isMounted = false
      clearTimeout(safetyTimeout)
      subscription.unsubscribe()
    }
  }, [])

  const loadProfile = async (userId: string, setLoadingState: boolean = false) => {
    try {
      if (setLoadingState) {
        setLoading(true)
      }
      
      console.log('Loading profile for user:', userId)
      const profileData = await getProfile(userId)
      
      if (profileData) {
        console.log('✅ Profile loaded:', profileData.display_name || profileData.email)
        setProfile(profileData)
      } else {
        console.log('No profile found, will be created on next update')
        setProfile(null)
      }
    } catch (error) {
      console.error('Error in loadProfile:', error)
      setProfile(null)
    } finally {
      if (setLoadingState) {
        setLoading(false)
      }
    }
  }

  const signUp = async (email: string, password: string, metadata: { displayName?: string; username?: string; [key: string]: any } = {}) => {
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: metadata.displayName || email.split('@')[0],
            username: metadata.username || email.split('@')[0].toLowerCase(),
            ...metadata
          }
        }
      })
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Sign up error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    try {
      console.log('Attempting to sign in:', email)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      if (data.session) {
        console.log('✅ Sign in successful, session created')
        console.log('Session expires at:', new Date(data.session.expires_at! * 1000).toLocaleString())
        console.log('Session stored in localStorage')
        
        // Don't set loading to false here - let onAuthStateChange handle it
        // This prevents race conditions
      }
      
      return { data, error: null }
    } catch (error) {
      console.error('❌ Sign in error:', error)
      // Only set loading to false on error since onAuthStateChange won't be called
      setLoading(false)
      return { data: null, error }
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setUser(null)
      setProfile(null)
      setSession(null)
    } catch (error) {
      console.error('Sign out error:', error)
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string) => {
    try {
      // Use current domain for redirects
      const redirectUrl = window.location.hostname === 'localhost' 
        ? `${window.location.origin}/reset-password`
        : `https://www.toefetti.ai/reset-password`
        
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      })
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Reset password error:', error)
      return { data: null, error }
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return null
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      
      if (error) throw error
      
      setProfile(data)
      return data
    } catch (error) {
      console.error('Update profile error:', error)
      return null
    }
  }

  const refreshProfile = async () => {
    if (!user) return
    await loadProfile(user.id)
  }

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    refreshProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 